class RcInfiniteScrolling extends HTMLElement {
  
  constructor() {
    super();
    this._observer = null;
    this._scrollHandler = null;
    this._isLoading = false;
    this._nextUrl = '';
    this._container = null;
    this._itemsContainer = null;
    this._loader = null;
    this._paginationData = null;
    this._sentinel = null;
  };

  connectedCallback() {this.initialize(); };

  disconnectedCallback() { this.deinitialize(); };

  initialize() {
    // Determine the logical root for scrolling: explicit root, host itself
    this._container = this.querySelector('[data-infinite-scroll-root]') || this;
    if(!this._container) return;

    // Find the items container; prefer generic selectors, then legacy ones, then fallbacks
    this._itemsContainer =
      this._container.querySelector('[data-infinite-scroll-items]') ||
      this._container.querySelector('[data-variant-list-items]') ||
      this._container.firstElementChild ||
      this._container;
    if(!this._itemsContainer) return;

    // Loader is optional; support generic and legacy data attributes
    this._loader =
      this._container.querySelector('[data-infinite-scroll-loader]') ||
      this._container.querySelector('[data-variant-list-loader]');

    // Pagination metadata can live either in a child element or on the host itself
    this._paginationData =
      this._container.querySelector('[data-infinite-scroll-pagination]') ||
      this._container.querySelector('[data-variant-list-pagination]') ||
      null;

    // Prefer host-level data-next-url for a more plug-and-play API
    this._nextUrl =
      this.getAttribute('data-next-url') ||
      (this._paginationData && this._paginationData.getAttribute('data-next-url')) ||
      '';
    if(!this._nextUrl) return;

    // Use an existing sentinel if provided, otherwise create one automatically
    this._sentinel =
      this._container.querySelector('[data-infinite-scroll-sentinel]') ||
      this._container.querySelector('[data-variant-list-sentinel]');
    if(!this._sentinel) {
      const sentinel = document.createElement('div');
      sentinel.setAttribute('data-infinite-scroll-sentinel', '');
      sentinel.style.width = '100%';
      sentinel.style.height = '1px';
      this._itemsContainer.appendChild(sentinel);
      this._sentinel = sentinel;
    };

    // Optional "Load more" button. When present (or data-mode="button"), the
    // component waits for a click instead of auto-loading on scroll.
    this._button = this.querySelector('[data-infinite-scroll-button]');
    this._mode = this.getAttribute('data-mode') || (this._button ? 'button' : 'scroll');

    if(this._mode === 'button' && this._button) {
      let _this = this;
      // Toggle visibility on the wrapper, not the button: the theme's
      // `button.button` rule outranks the `.hidden` utility on specificity,
      // so `.hidden` only reliably hides a plain element.
      this._buttonWrap =
        this._button.closest('[data-infinite-scroll-button-wrap]') || this._button;
      this._buttonHandler = function() { _this.loadMore(); };
      this._button.addEventListener('click', this._buttonHandler);
      this.updateButtonState();
      return;
    };

    let rootMarginAttr = this.getAttribute('data-root-margin');
    this._rootMargin = rootMarginAttr || '200px 0px';
    this.setupObserver();
  };

  updateButtonState() {
    let target = this._buttonWrap || this._button;
    if(!target) return;
    this._nextUrl
      ? target.classList.remove('hidden')
      : target.classList.add('hidden');
  };
  
  deinitialize() {
    if(this._observer && this._sentinel) this._observer.unobserve(this._sentinel);
    if(this._observer) {
      this._observer.disconnect();
      this._observer = null;
    };
    if(this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      this._scrollHandler = null;
    };
    if(this._button && this._buttonHandler) {
      this._button.removeEventListener('click', this._buttonHandler);
      this._buttonHandler = null;
    };
  };

  setLoading(state) {
    this._isLoading = state;
    if(!this._loader) return;
    state ? this._loader.classList.remove('hidden') : this._loader.classList.add('hidden');
  };

  loadMore() {
    let _this = this;
    if(!this._nextUrl || this._isLoading) return;
    this.setLoading(true);
    if(this._button) this._button.setAttribute('disabled', '');
    fetch(this._nextUrl, { credentials: 'same-origin' })
    .then(function(response) {
      return response.text();
    })
    .then(function(html) {
      let parser = new DOMParser();
      let doc = parser.parseFromString(html, 'text/html');
      let infiniteId = _this.getAttribute('data-infinite-scroll-id');
      let selector = infiniteId
        ? 'rc-infinite-scrolling[data-infinite-scroll-id="' + infiniteId + '"]'
        : 'rc-infinite-scrolling';
      let newHost = doc.querySelector(selector);
      if(!newHost) {
        _this._nextUrl = '';
        return;
      };
      let newContainer = newHost.querySelector('[data-infinite-scroll-root]') || newHost;
      if(!newContainer) {
        _this._nextUrl = '';
        return;
      };
      let newItemsContainer =
        newContainer.querySelector('[data-infinite-scroll-items]') ||
        newContainer.querySelector('[data-variant-list-items]') ||
        newContainer.firstElementChild ||
        newContainer;
      let newPaginationData =
        newContainer.querySelector('[data-infinite-scroll-pagination]') ||
        newContainer.querySelector('[data-variant-list-pagination]');
      if(newItemsContainer) {
        let child = newItemsContainer.firstElementChild;
        while(child) {
          let nextSibling = child.nextElementSibling;
          const isSentinel =
            (child.hasAttribute && child.hasAttribute('data-infinite-scroll-sentinel')) ||
            (child.hasAttribute && child.hasAttribute('data-variant-list-sentinel'));
          isSentinel
            ? child.remove()
            : _this._itemsContainer.insertBefore(child, _this._sentinel);
          child = nextSibling;
        };
      };
      // On subsequent pages, prefer host-level data-next-url if present
      _this._nextUrl =
        newHost.getAttribute('data-next-url') ||
        (newPaginationData && newPaginationData.getAttribute('data-next-url')) ||
        '';
      if(!_this._nextUrl && _this._observer && _this._sentinel) _this._observer.unobserve(_this._sentinel);
    })
    .catch(function(error) {
      showToast('Error loading more items.', 'error');
    })
    .finally(function() {
      _this.setLoading(false);
      if(_this._button) {
        _this._button.removeAttribute('disabled');
        _this.updateButtonState();
      };
    });
  };

  setupObserver() {
    let _this = this;
    if('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        function(entries) {
          entries.forEach(function(entry) {
            if(entry.isIntersecting && _this._nextUrl && !_this._isLoading) _this.loadMore();
          });
        },
        {
          root: null,
          rootMargin: this._rootMargin,
          threshold: 0
        }
      );
      this._observer.observe(this._sentinel);
    } else {
      this._scrollHandler = function() {
        if(!_this._nextUrl || _this._isLoading) return;
        let rect = _this._sentinel.getBoundingClientRect();
        let viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        if(rect.top - viewportHeight < 200) _this.loadMore();
      };
      window.addEventListener('scroll', this._scrollHandler);
    };
  };

};

if(!customElements.get('rc-infinite-scrolling')) {
  customElements.define('rc-infinite-scrolling', RcInfiniteScrolling);
};